import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { SeatData, Viewer, ViewerMouseEvent } from 'src/assets/lib/types';
import RowTracker from '../core/models/rowTracker';
import TicketType from '../core/models/ticket.type';

export type SelectedSeatData = {
  seatIndex: number;
  prefix: string;
  blockId: string;
  selected: boolean;
};

export type TicketTypeWithBlockId = Omit<TicketType, 'block_id'> & {
  block_id: string;
};

export type RowTrackerWithQtyPerOrder = RowTracker & {
  min_per_order: number;
  max_per_order: number;
  taken: number;
};

type TicketTypeSeatLimits = Pick<
  TicketType,
  'max_tickets_per_order' | 'min_tickets_per_order'
> & {
  taken: number;
};

const SELECTED_SEAT_BG = '#255F38';
const BLOCKED_SEAT_BG = '#fff';

@Injectable({
  providedIn: 'root',
})
export class ThreeDMapService {
  private viewer!: Viewer;
  private panoEl!: HTMLElement;
  private tooltipEl!: HTMLElement;
  private alertEl!: HTMLElement;
  private selectedSeatSubject: BehaviorSubject<SelectedSeatData | null>;
  private isPanoViewActiveSubject: BehaviorSubject<boolean | null>;
  private trackers: Record<string, RowTracker & { ticketTypeId: number }>;
  private ticketTypeSeatLimits: Record<number, TicketTypeSeatLimits>;
  private ticketTypeNames: Record<string, string>;
  private blockedSeatIds: Record<string, boolean>;
  private blockIdsPrice: Record<string, number>;
  private selectedSeats: Set<string>;

  public selectedSeat: Observable<SelectedSeatData | null>;
  public isPanoViewActive: Observable<boolean | null>;

  constructor() {
    this.selectedSeatSubject = new BehaviorSubject<SelectedSeatData | null>(
      null
    );
    this.ticketTypeNames = {};
    this.selectedSeat = this.selectedSeatSubject.asObservable();
    this.selectedSeats = new Set();
    this.trackers = {};
    this.ticketTypeSeatLimits = {};
    this.blockIdsPrice = {};
    this.blockedSeatIds = {};
    this.isPanoViewActiveSubject = new BehaviorSubject<boolean | null>(null);
    this.isPanoViewActive = this.isPanoViewActiveSubject.asObservable();
  }

  public init(ticketTypes: TicketTypeWithBlockId[]) {
    this.checkViews();
    this.selectedSeats = new Set();
    this.trackers = {};
    this.ticketTypeSeatLimits = {};
    this.blockIdsPrice = {};
    this.blockedSeatIds = {};
    this.setupTrackersAndPrices(ticketTypes);
    this.hideTooltip();
    this.hideAlert();

    this.viewer = new ((window as any).Pacifa3d as any).Viewer(
      'stadiumView',
      'Gp4Xv-z6a4F-hG5bA',
      {}
    );

    this.viewer.on('blockClicked', this.handleBlockClicked.bind(this));
    this.viewer.on('blockMouseMove', this.handleBlockMouseMove.bind(this));
    this.viewer.on('blockMouseLeave', () => this.hideTooltip());

    this.viewer.on('seatClicked', this.handleSeatClicked.bind(this));
    this.viewer.on('seatMouseMove', this.handleSeatMouseMove.bind(this));
    this.viewer.on('seatMouseLeave', () => this.hideTooltip());

    this.displayTopView();
  }

  public async displayTopView(hidePano: boolean = true) {
    if (hidePano) {
      this.hidePano();
    }

    await this.viewer.displayTopView({});
  }

  public displayPanoView() {}

  public updateTracker(blockId: string, tracker: RowTracker) {
    const deselectedSeats: string[] = [];
    const oldTracker = this.trackers[tracker.prefix];
    for (let i = 0; i < oldTracker.str_rbitmap.length; i++) {
      const seatId = oldTracker.prefix + (i + 1);

      const isNewFree = tracker.str_rbitmap[i] === '0';
      const isOldFree = oldTracker.str_rbitmap[i] === '0';

      const isNewOccupied = tracker.str_rbitmap[i] === '1';
      const isOldOccupied = oldTracker.str_rbitmap[i] === '1';

      const isStillFree = isNewFree && isOldFree;
      if (isStillFree) continue;

      const isNowFree = isOldOccupied && isNewFree;
      if (isNowFree) {
        this.updateSeatStatus(seatId, 'free');
      }

      const isNowOccupied = isOldFree && isNewOccupied;
      if (isNowOccupied) {
        this.updateSeatStatus(seatId, 'occupied');
      }

      const isSelected = this.selectedSeats.has(seatId);
      if (isSelected && isNowOccupied) {
        const { prefix, seatIndex } = this.getSeatPrefixAndIndex(seatId)!;
        this.updateSeatStatus(seatId, 'free');
        this.updateSeatStatus(seatId, 'occupied');
        this.selectedSeatSubject.next({
          prefix,
          seatIndex,
          selected: false,
          blockId,
        });
        deselectedSeats.push(seatId);
      }
    }

    oldTracker.str_rbitmap = tracker.str_rbitmap;
    oldTracker.str_blocked_rbitmap = tracker.str_blocked_rbitmap;

    if (deselectedSeats.length === 0) return;

    const seats: string[] = [];
    deselectedSeats.forEach((seatId) => {
      seats.push(
        `${blockId.replace('_', ' ')} Asiento ${seatId.replace(/\w+_/, '')}`
      );
    });

    let content = `
      <strong>Atención:</strong> Los siguientes asientos ya han sido seleccionados:
      <ul style="margin: 0; padding-left: 20px;">
        ${seats.map((seat) => `<li>${seat}</li>`).join('')}
      </ul>
    `;

    this.showAlert(content);
  }

  private async updateSeatStatus(
    seatId: string,
    newStatus: 'free' | 'occupied' | 'selected'
  ) {
    const seat = this.viewer.getSeatElementById(seatId);
    if (newStatus === 'occupied') {
      if (seat) seat.style.fill = BLOCKED_SEAT_BG;
      this.blockedSeatIds[seatId] = true;
    } else if (newStatus === 'free') {
      if (seat) {
        seat.style.fill = '';
        seat.style.strokeWidth = '0px';
        seat.style.stroke = '#000';
      }
      this.blockedSeatIds[seatId] = false;
      this.selectedSeats.delete(seatId);
    } else {
      if (seat) {
        seat.style.fill = SELECTED_SEAT_BG;
        seat.style.strokeWidth = '10px';
        seat.style.stroke = '#000';
      }
      this.selectedSeats.add(seatId);
    }
  }

  private handleBlockMouseMove(blockId: string, ev: ViewerMouseEvent) {
    let content = `Sección: ${this.ticketTypeNames[blockId].replace('_', ' ')}`;
    this.showTooltip(ev, content);
  }

  private handleSeatMouseMove(seat: SeatData, ev: ViewerMouseEvent) {
    const tixSeatData = this.getSeatPrefixAndIndex(seat.seatId);
    if (!tixSeatData) return;
    let content: string;
    if (this.blockedSeatIds[seat.seatId]) {
      content = 'Asiento Ocupado';
    } else {
      const name = this.ticketTypeNames[seat.blockId]
      content = `Sección: ${name.replace('_', ' ')}<br/>Fila: ${seat.row}<br/>Asiento: ${tixSeatData.seatIndex}<br/>Precio: RD$${this.blockIdsPrice[seat.blockId]}`;
    }
    this.showTooltip(ev, content);
  }

  private async handleBlockClicked(blockId: string) {
    this.hideTooltip();
    this.panoEl.style.display = 'block';
    this.isPanoViewActiveSubject.next(true);

    await this.viewer.displayBlockView(blockId, {});
    await this.viewer.displayBlockPano(blockId, this.panoEl);
    this.setupSeats(blockId);
  }

  private async setupSeats(blockId: string) {
    const seatIds = await this.viewer.getSeatIds(blockId);

    seatIds.forEach((seatId) => {
      const tixSeatData = this.getSeatPrefixAndIndex(seatId);
      if (!tixSeatData) return;
      const tracker = this.trackers[tixSeatData.prefix];

      const isOccupied = this._isBlockedOrSold(
        tracker,
        tixSeatData.seatIndex - 1
      );

      const seatEl = this.viewer.getSeatElementById(seatId)!;
      if (isOccupied) {
        seatEl.style.fill = BLOCKED_SEAT_BG;
      }

      const isSelected = this.selectedSeats.has(seatId);
      if (isSelected) {
        this.updateSeatStatus(seatId, 'selected');
      }
    });
  }

  private _isBlockedOrSold(rowTracker: RowTracker, seatIndex: number) {
    return (
      rowTracker.str_rbitmap[seatIndex] === '1' ||
      rowTracker.str_blocked_rbitmap[seatIndex] === '1'
    );
  }

  private hidePano() {
    this.viewer.hidePano(this.panoEl);
    this.panoEl.style.display = 'none';
    this.isPanoViewActiveSubject.next(false);
  }

  private handleSeatClicked(seatData: SeatData) {
    this.hideTooltip();
    if (this.blockedSeatIds[seatData.seatId]) return;

    const tixSeatData = this.getSeatPrefixAndIndex(seatData.seatId);
    if (!tixSeatData) return;
    const { prefix, seatIndex } = tixSeatData;

    const alreadySelected = this.selectedSeats.has(seatData.seatId);

    const tracker = this.trackers[prefix];
    const seatLimits = this.ticketTypeSeatLimits[tracker.ticketTypeId];

    if (
      !alreadySelected &&
      seatLimits.taken >= seatLimits.max_tickets_per_order
    ) {
      let content = ` <strong>Atención:</strong> Has alcanzado el límite máximo de asientos permitidos para este bloque (${seatLimits.max_tickets_per_order}).`;
      this.showAlert(content);
      return;
    }

    seatLimits.taken = alreadySelected
      ? seatLimits.taken - 1
      : seatLimits.taken + 1;

    this.trackers[prefix];
    this.viewer.displaySeatPano(seatData.blockId, seatData.seatId, this.panoEl);

    this.updateSeatStatus(
      seatData.seatId,
      alreadySelected ? 'free' : 'selected'
    );

    if (!alreadySelected) {
      const ticketTypeName = this.ticketTypeNames[seatData.blockId];
      let content = `Asiento seleccionado: ${ticketTypeName.replace('_', ' ')} - Asiento ${seatData.row}${tixSeatData.seatIndex} - RD$${this.blockIdsPrice[seatData.blockId]}`;
      this.showAlert(content, 'alert-success');
    }

    this.selectedSeatSubject.next({
      prefix,
      seatIndex: Number(seatIndex),
      selected: !alreadySelected,
      blockId: seatData.blockId,
    });
  }

  private checkViews() {
    const stadiumEl = document.getElementById('stadiumView');
    if (!stadiumEl) {
      throw new Error('Stadium not found');
    }

    const panoEl = document.getElementById('panoDisplay');
    if (!panoEl) {
      throw new Error('Pano Display not found');
    }

    this.panoEl = panoEl;

    this.tooltipEl = document.getElementById('tooltip')!;
    this.alertEl = document.getElementById('3d-seats-taken-alert')!;
  }

  private setupTrackersAndPrices(ticketTypes: TicketTypeWithBlockId[]) {
    const cpyTicketTypes = structuredClone(
      ticketTypes
    ) as TicketTypeWithBlockId[];
    cpyTicketTypes.forEach((tt) => {
      this.ticketTypeSeatLimits[tt.id] = {
        min_tickets_per_order: tt.min_tickets_per_order,
        max_tickets_per_order: tt.max_tickets_per_order,
        taken: 0,
      };

      this.ticketTypeNames[tt.block_id] = tt.name;
      this.blockIdsPrice[tt.block_id] = tt.price;
      tt.rows_trackers.forEach((tracker) => {
        this.trackers[tracker.prefix] = {
          ...tracker,
          ticketTypeId: tt.id,
        };
        this.insertBlockedSeatIds(tracker);
      });
    });
  }

  private insertBlockedSeatIds(tracker: RowTracker) {
    for (let seatIndex = 0; seatIndex < tracker.capacity; seatIndex++) {
      const isOccupied = this._isBlockedOrSold(tracker, seatIndex);
      const seatId = tracker.prefix + (seatIndex + 1);
      if (isOccupied) {
        this.blockedSeatIds[seatId] = true;
      }
    }
  }

  private getSeatPrefixAndIndex(seatId: string) {
    const matched = seatId.match(/(\w+_[a-zA-Z]+)(\d+$)/);
    if (!matched) return null;
    return { prefix: matched[1], seatIndex: Number(matched[2]) };
  }

  private hideAlert() {
    this.alertEl.style.display = 'none';
  }

  private showAlert(content: string, style = 'alert-warning') {
    this.alertEl.style.display = 'block';
    this.alertEl.classList.add(style);
    this.alertEl.innerHTML = content;
    setTimeout(() => {
      this.hideAlert();
    }, 5000);
  }

  private hideTooltip() {
    this.tooltipEl.style.display = 'none';
  }

  private showTooltip(ev: ViewerMouseEvent, content: string) {
    const globalX = this.viewer.containerEl.offsetLeft + ev.clientX;
    const globalY = this.viewer.containerEl.offsetTop + ev.clientY;

    this.tooltipEl.style.display = 'block';
    this.tooltipEl.innerHTML = content;
    this.tooltipEl.style.zIndex = '1000';
    this.tooltipEl.style.left = globalX + 10 + 'px';
    this.tooltipEl.style.top = globalY + 10 + 'px';
  }
}
